import React, { useEffect, useMemo, useState } from "react";
import { Search, Users, Lock, Eye, CheckSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { UserAvatar } from "../UserAvatar";

/**
 * Modale condivisione checklist (solo owner).
 * - interruttore "tutta l'organizzazione"
 * - oppure ricerca + selezione utenti del tenant
 * - permesso: can_check (spuntano) | view_only (sola lettura)
 */
export const ShareModal = ({ list, onClose, onSaved }) => {
  const [colleagues, setColleagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [orgWide, setOrgWide] = useState(list?.sharing === "org");
  const [selected, setSelected] = useState(new Set(list?.shared_user_ids || []));
  const [permission, setPermission] = useState(list?.shared_permission || "can_check");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/checklists/colleagues")
      .then(({ data }) => setColleagues(data))
      .catch(() => toast.error("Impossibile caricare gli utenti"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return colleagues;
    return colleagues.filter((u) =>
      (u.full_name || u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q)
    );
  }, [colleagues, query]);

  const toggleUser = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const sharing = orgWide ? "org" : selected.size > 0 ? "users" : "private";
      await api.post(`/checklists/${list.id}/share`, {
        sharing,
        shared_user_ids: orgWide ? [] : Array.from(selected),
        shared_permission: permission,
      });
      toast.success(
        sharing === "private" ? "Lista resa privata"
        : sharing === "org" ? "Lista condivisa con tutta l'organizzazione"
        : `Lista condivisa con ${selected.size} ${selected.size === 1 ? "utente" : "utenti"}`
      );
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella condivisione");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-xl bg-surface sm:max-w-md" data-testid="checklist-share-modal">
        <DialogHeader>
          <DialogTitle>Condividi «{list?.titolo}»</DialogTitle>
          <DialogDescription>
            I destinatari vedono la lista; con «possono spuntare» segnano le voci fatte
            (col loro nome), ma non modificano mai testo o struttura.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Org-wide switch */}
          <div className="flex items-center justify-between rounded-lg border border-border bg-subtle px-4 py-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <Users size={16} className="text-accent shrink-0" />
              <div>
                <div className="text-sm font-medium text-text-primary">Tutta l'organizzazione</div>
                <div className="text-[11px] text-text-muted">Visibile a tutti i colleghi del tenant</div>
              </div>
            </div>
            <Switch checked={orgWide} onCheckedChange={setOrgWide} data-testid="share-orgwide-switch" />
          </div>

          {/* Selezione utenti */}
          {!orgWide && (
            <div>
              <div className="relative mb-2">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Cerca un collega…"
                  className="h-10 pl-9 rounded-md border-border bg-surface"
                  data-testid="share-user-search"
                />
              </div>
              <div className="max-h-44 overflow-y-auto rounded-lg border border-border divide-y divide-border">
                {loading ? (
                  <div className="p-4 text-sm text-text-muted flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Caricamento…</div>
                ) : filtered.length === 0 ? (
                  <div className="p-4 text-sm text-text-muted">Nessun collega trovato</div>
                ) : filtered.map((u) => (
                  <label key={u.id} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-subtle transition-colors duration-150" data-testid={`share-user-${u.id}`}>
                    <input
                      type="checkbox"
                      checked={selected.has(u.id)}
                      onChange={() => toggleUser(u.id)}
                      className="w-4 h-4 accent-current"
                      style={{ accentColor: "var(--wl-accent, #1a1a1a)" }}
                    />
                    <UserAvatar userId={u.id} name={u.full_name || u.name || u.email} size={26} />
                    <div className="min-w-0">
                      <div className="text-sm text-text-primary truncate">{u.full_name || u.name || u.email}</div>
                      <div className="text-[11px] text-text-muted truncate">{u.email}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Permesso */}
          <div>
            <div className="text-xs font-medium text-text-secondary mb-2">Permesso dei destinatari</div>
            <div className="grid grid-cols-2 gap-2">
              <PermissionOption
                active={permission === "can_check"}
                onClick={() => setPermission("can_check")}
                icon={CheckSquare}
                title="Possono spuntare"
                subtitle="Nome e orario registrati"
                testId="share-perm-cancheck"
              />
              <PermissionOption
                active={permission === "view_only"}
                onClick={() => setPermission("view_only")}
                icon={Eye}
                title="Sola lettura"
                subtitle="Vedono senza spuntare"
                testId="share-perm-viewonly"
              />
            </div>
          </div>

          {!orgWide && selected.size === 0 && (
            <div className="flex items-center gap-2 text-[11px] text-text-muted">
              <Lock size={12} /> Nessun utente selezionato: la lista resterà privata.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} className="bg-accent hover:bg-accent-strong text-white" data-testid="share-save-btn">
            {saving ? "Salvataggio…" : "Salva condivisione"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PermissionOption = ({ active, onClick, icon: Icon, title, subtitle, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`text-left rounded-lg border px-3 py-2.5 transition-colors duration-150 ${
      active ? "border-accent bg-accent-subtle" : "border-border bg-surface hover:bg-subtle"
    }`}
  >
    <div className="flex items-center gap-1.5 text-sm font-medium text-text-primary">
      <Icon size={14} className={active ? "text-accent" : "text-text-muted"} /> {title}
    </div>
    <div className="text-[11px] text-text-muted mt-0.5">{subtitle}</div>
  </button>
);
